(ns client-portal.client.products.data
  (:require [clojure.string :as s]
            [shared.core.account.data :as account.d]
            [shared.core.reactive :as re]
            [shared.core.router :as router]
            [shared.core.util :as util]))

(def root-path [:products])
(def list-data-path (conj root-path :list :data))
(def selected-path (conj root-path :selected))
(def google-cloud-projects-form-path (conj root-path :google-cloud-projects-form))
(def google-cloud-projects-storage-path (conj root-path :google-cloud-projects))

(def google-cloud-project-options
  [{:text "Default" :value ""}
   {:text "soundhound-dev-data-prj" :value "soundhound-dev-data-prj"}
   {:text "soundhound-uat-data-prj" :value "soundhound-uat-data-prj"}
   {:text "soundhound-prod-data-prj" :value "soundhound-prod-data-prj"}])

(defn list-data
  "The tenant's products ({:label :reference-id}, label order), or nil while loading.
   `:reference-id` is what identifies a product to the server."
  [db]
  (util/when-not-loading (get-in db list-data-path)))

(defn reference-ids [products]
  (into #{} (keep :reference-id) products))

(def reference-id->project-param
  {"oasys" :oasys-proj
   "voice-commerce" :vc-proj})

(defn project-params
  "The page's BigQuery project overrides (`?oasys-proj=`, `?vc-proj=`) for the products `reference-ids`, as request
   params"
  [db reference-ids]
  (->> (keep reference-id->project-param reference-ids)
       (select-keys (router/current-query-params db))
       (filter (comp not-empty val))
       (into {})))

(defn- admin? [db]
  (account.d/has-every-role? (account.d/account-user-roles db) [account.d/admin-role]))

(defn- stored-google-cloud-projects [db]
  (get-in (:local-storage db) google-cloud-projects-storage-path))

;; The URL wins whenever it carries an override; a URL with none gets the stored ones back, so they're cleared through
;; the dialog rather than by editing the URL
(re/reg-event-fx
  :products/sync-google-cloud-projects re/middleware
  (fn [{:keys [db local-storage]} _]
    (when (admin? db)
      (let [url-projects (project-params db ["oasys" "voice-commerce"])
            stored (stored-google-cloud-projects db)]
        (cond
          (and (not-empty url-projects) (not= url-projects stored))
          {:local-storage (assoc-in local-storage google-cloud-projects-storage-path url-projects)}

          (and (empty? url-projects) (not-empty stored))
          {:dispatch [:router/go-with-merged-query-params stored]})))))

(re/reg-event-db
  :products/open-google-cloud-projects re/middleware
  (fn [db]
    (re/dispatch [:dialog/show-with-form-init
                  [:products/google-cloud-projects-dialog]
                  google-cloud-projects-form-path
                  (merge {:oasys-proj ""
                          :vc-proj ""}
                         (project-params db ["oasys" "voice-commerce"]))])
    db))

;; Loaded data is cached per concept/report, so the page reloads to refetch it all under the new projects
(re/reg-event-fx
  :products/save-google-cloud-projects re/middleware
  (fn [{:keys [db local-storage]} _]
    (let [form (get-in db google-cloud-projects-form-path)
          projects (->> (select-keys form [:oasys-proj :vc-proj])
                        (filter (comp not-empty val))
                        (into {}))]
      (if (= projects (project-params db ["oasys" "voice-commerce"]))
        {:dispatch [:dialog/hide]}
        {:local-storage (assoc-in local-storage google-cloud-projects-storage-path projects)
         :dispatch [:router/go-with-merged-query-params
                    (merge {:oasys-proj nil
                            :vc-proj nil}
                           projects)
                    {:on-route-change #(.reload js/location)}]}))))

(re/reg-auth0-loader
  :products/load-list-data
  list-data-path
  (fn [_ _]
    {:method :get
     :url "/api/products"})
  :transf (fn [resp] (or (some->> resp
                                  :body
                                  (util/->kebab-case-keywords)
                                  (mapv #(select-keys % [:label :reference-id]))
                                  (sort-by :label)
                                  (vec))
                         []))
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :products/list-
  list-data-path
  :products/load-list-data)

(re/reg-sub
  :products/reference-ids-
  :<- [:products/list-]
  (fn [products]
    (reference-ids (util/when-not-loading products))))

(re/reg-event-db
  :products/select re/middleware
  (fn [db [product]]
    (assoc-in db selected-path product)))

(defn selected-product [db]
  (get-in db selected-path))

(re/reg-sub :products/selected- selected-product)

(defn selected-product-by-route [db]
  (let [cur-route (router/current-route-name db)
        route-starts-with? #(or (= (some-> cur-route (name)) %)
                                (some-> cur-route (name) (s/starts-with? (str % "-")))
                                (some-> cur-route (namespace) (s/starts-with? %)))]
    (cond
      (route-starts-with? "vi")
      "voice-insights"

      (route-starts-with? "vc")
      "voice-commerce"

      (route-starts-with? "so")
      "smart-ordering"

      :else
      nil)))
